import { useEffect, useId, useState } from "react";
import { useGetList, type Identifier } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { Contact } from "@/components/atomic-crm/types";
import type { ClientCreateFormValues } from "@/modules/clients/ClientCreateForm";
import { getContactFullName } from "@/modules/clients/clientShowUtils";
import { contactBillingChannels } from "../../../supabase/functions/_shared/clientRecipientPolicy.ts";

const sameId = (a: Identifier, b: Identifier) => String(a) === String(b);

/**
 * Account billing recipients: the primary contact by default, or any
 * contacts of this account. Stores contact references only — each contact's
 * current billing email/phone is resolved at send time.
 */
export const CompanyBillingContactsField = ({
  companyId,
}: {
  companyId: Identifier;
}) => {
  const id = useId();
  const { setValue } = useFormContext<ClientCreateFormValues>();
  const selected =
    useWatch<ClientCreateFormValues, "billing_contact_ids">({
      name: "billing_contact_ids",
    }) ?? [];
  const invoiceSameAsPrimary = useWatch<
    ClientCreateFormValues,
    "invoice_same_as_primary"
  >({ name: "invoice_same_as_primary" });
  const legacyInvoiceEmail = useWatch<ClientCreateFormValues, "invoice_email">({
    name: "invoice_email",
  });
  const { data: contacts = [], isPending } = useGetList<Contact>("contacts", {
    filter: { "company_id@eq": companyId },
    sort: { field: "first_name", order: "ASC" },
    pagination: { page: 1, perPage: 200 },
  });

  const [usePrimary, setUsePrimary] = useState(selected.length === 0);
  useEffect(() => {
    if (selected.length > 0) setUsePrimary(false);
  }, [selected.length]);

  const setSelected = (ids: Identifier[]) =>
    setValue("billing_contact_ids", ids, { shouldDirty: true });

  const toggleUsePrimary = (checked: boolean) => {
    setUsePrimary(checked);
    if (!checked) return;
    setSelected([]);
    // Also retire a legacy manually typed invoice contact.
    setValue("invoice_same_as_primary", true, { shouldDirty: true });
  };

  const toggleContact = (contactId: Identifier, checked: boolean) =>
    setSelected(
      checked
        ? [...selected, contactId]
        : selected.filter((value) => !sameId(value, contactId)),
    );

  const missingIds = isPending
    ? []
    : selected.filter(
        (value) => !contacts.some((contact) => sameId(contact.id, value)),
      );
  const legacyEmail =
    invoiceSameAsPrimary === false && selected.length === 0
      ? legacyInvoiceEmail?.trim()
      : "";

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Switch
          id={`${id}-primary`}
          checked={usePrimary}
          onCheckedChange={toggleUsePrimary}
        />
        <Label htmlFor={`${id}-primary`} className="cursor-pointer">
          Send billing to the primary contact
        </Label>
      </div>

      {legacyEmail ? (
        <p className="text-xs text-muted-foreground">
          Billing currently goes to {legacyEmail} (entered manually). Select
          contacts below to replace it.
        </p>
      ) : null}

      {!usePrimary ? (
        <div className="space-y-2">
          {contacts.map((contact) => {
            const checked = selected.some((value) => sameId(value, contact.id));
            const emails = contactBillingChannels(contact).emails;
            return (
              <label key={contact.id} className="flex items-start gap-2">
                <Checkbox
                  className="mt-0.5"
                  checked={checked}
                  onCheckedChange={(value) =>
                    toggleContact(contact.id, value === true)
                  }
                />
                <span className="min-w-0 text-sm">
                  <span className="block truncate">
                    {getContactFullName(contact)}
                  </span>
                  <span
                    className={
                      emails.length
                        ? "block truncate text-xs text-muted-foreground"
                        : "block text-xs text-destructive"
                    }
                  >
                    {emails.length ? emails.join(", ") : "No email on file"}
                  </span>
                </span>
              </label>
            );
          })}
          {!isPending && contacts.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              This account has no contacts yet.
            </p>
          ) : null}
          {missingIds.length ? (
            <div className="flex items-center gap-2 text-xs text-destructive">
              <AlertTriangle className="size-3.5 shrink-0" />
              <span>
                {missingIds.length === 1
                  ? "A billing contact was deleted or moved to another account."
                  : `${missingIds.length} billing contacts were deleted or moved to another account.`}
              </span>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() =>
                  setSelected(
                    selected.filter(
                      (value) =>
                        !missingIds.some((gone) => sameId(gone, value)),
                    ),
                  )
                }
              >
                Remove
              </Button>
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Each contact receives billing at the details marked for billing on
            their profile, or their primary email and phone.
          </p>
        </div>
      ) : null}
    </div>
  );
};
